import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Input, MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { Add20Regular, Building24Regular, DocumentArrowUp20Regular, Search20Regular } from '@fluentui/react-icons';
import { useBoolean } from '@fluentui/react-hooks';
import { useTranslation } from 'react-i18next';

import { Provider } from '../../../models/Entities/Provider/Provider';
import { ProviderFormComponent } from '../ProviderForm/ProviderFormComponent';
import { ProviderFromInvoiceDrawer } from '../ProviderFromInvoice/ProviderFromInvoice.Drawer';
import { FileDropZone } from '../../Commons/FileDropZone/FileDropZone.Component';
import { INBOX_ACCEPT } from '../../../functions/ProviderInbox.Functions';

// guía
import { GuideTable, GuideTh, GuideRowActions, guideCellClassName as cell, guideRowClassName, useGuideSort } from '../../../theme/components/GuideTable';
import { GuidePagination, useGuidePagination } from '../../../theme/components/GuidePagination';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideBannerAction, GuideLinkButton } from '../../../theme/components/GuideButtons';
import { GuideCountChip } from '../../../theme/components/GuideKpiCard';
import { StatusPill } from '../../../theme/components/StatusPill';

// lógica y props
import * as ComponentFunctions from './ProvidersListComponentFunctions';
import { filterProviders, providerIdentifier, providerInitials, providerName, providerPersonKind } from './ProvidersList.Logic';

// styles
import './ProvidersList.Styles.css';

const PAGE_SIZES = [10, 25, 50, 100];

/**
 * Lista de proveedores según la guía: barra con búsqueda, contador y "Agregar proveedor" a la
 * derecha; grilla con encabezado en mayúsculas, orden por columna, acciones en ⋯ y paginación
 * al pie. Estados de carga (skeleton), error (banner con "Reintentar") y vacío (recuadro punteado).
 */
export const ProvidersListComponent = (props: ComponentFunctions.Props) => {
    const navigate = useNavigate();
    const [t] = useTranslation();

    const [search, setSearch] = useState<string>('');
    const [selectedProvider, setSelectedProvider] = useState<Provider | undefined>(undefined);
    const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);

    // ---- alta desde factura (spec 009, US4) ----
    const [fromInvoiceOpen, setFromInvoiceOpen] = useState<boolean>(false);
    const [invoiceBatch, setInvoiceBatch] = useState<{ id: number; files: File[] } | undefined>(undefined);
    const invoiceInput = useRef<HTMLInputElement>(null);
    const canAddFromInvoice = !!props.IdentifyProviderInvoice && !!props.ResolveProviderInbox && !!props.DiscardProviderInbox;

    // Siempre se pide la lista completa al entrar: el detalle de un proveedor (abierto por URL o desde un
    // consumo importado) deja en el store sólo ese proveedor, y una carga fallida deja successLoadingAll
    // en false; con la condición anterior (lista vacía y nunca cargada) la pantalla quedaba con uno o
    // ningún proveedor.
    const load = () => props.GetAllProviders(1, 1000, undefined);
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const all: Provider[] = props.Providers || [];
    const filtered = useMemo(() => filterProviders(all, search), [all, search]);

    const { rows, sortProps } = useGuideSort<Provider>(filtered, {
        name: providerName,
        identifier: (provider) => providerIdentifier(provider) || undefined,
        type: (provider) => providerPersonKind(provider)
    }, { key: 'name', direction: 'asc' });

    const { pageRows, paginationProps } = useGuidePagination(rows, 25);

    // events
    const onSearch = (value: string) => {
        setSearch(value);
        paginationProps.onPageChange(1);
    };

    const onAdd = () => {
        setSelectedProvider(undefined);
        openPanel();
    };

    const onEdit = (provider: Provider) => {
        setSelectedProvider(provider);
        openPanel();
    };

    const onCloseForm = () => {
        dismissPanel();
        setSelectedProvider(undefined);
    };

    const onView = (provider: Provider) => navigate('/providers/' + provider.id);

    /** Archivos soltados o elegidos: abren "Alta desde factura" (cada lote con un id nuevo). */
    const onInvoiceFiles = (files: File[]) => {
        if (files.length === 0) return;
        setInvoiceBatch((current) => ({ id: (current?.id ?? 0) + 1, files }));
        setFromInvoiceOpen(true);
    };

    const typeLabel = (provider: Provider): string | undefined => {
        switch (providerPersonKind(provider)) {
            case 'natural': return t('providerList.personType.natural');
            case 'juridical': return t('providerList.personType.juridical');
            default: return undefined;
        }
    };

    const isSearching = search.trim().length > 0;
    const showError = props.failOnLoadingAll || props.failOnUpdatingAll;
    const showSkeleton = props.isLoadingAll && all.length === 0;

    const renderRow = (provider: Provider) => {
        const name = providerName(provider);
        const identifier = providerIdentifier(provider);
        const kind = typeLabel(provider);
        return (
            <tr
                key={provider.id}
                className={guideRowClassName({}, 'pvl-row')}
                tabIndex={0}
                onClick={() => onView(provider)}
                onKeyDown={(event) => { if (event.key === 'Enter') onView(provider); }}
                data-testid={`pvl-row-${provider.id}`}
            >
                <td>
                    <div className="pvl-name">
                        <span className="pvl-name__avatar" aria-hidden="true">{providerInitials(provider)}</span>
                        <div className="pvl-name__text">
                            <span className="pvl-name__title">{name}</span>
                            <span className="pvl-name__sub">#{provider.id}</span>
                        </div>
                    </div>
                </td>
                <td className={cell('num')}>
                    {identifier || <span className="pvl-muted">—</span>}
                </td>
                <td>
                    {kind
                        ? <StatusPill tone="neutral" symbol="○">{kind}</StatusPill>
                        : <span className="pvl-muted">—</span>}
                </td>
                <td className={cell('actions')} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                    <GuideRowActions
                        label={t('providerList.actions.rowActions', { name })}
                        actions={[
                            { key: 'view', label: t('providerList.actions.view'), onClick: () => onView(provider) },
                            { key: 'edit', label: t('providerList.props.buttons.modifyButton'), onClick: () => onEdit(provider) },
                            { key: 'contact', label: t('providerList.actions.contact'), onClick: () => navigate(`/providers/${provider.id}?tab=contact`) },
                            { key: 'agreements', label: t('providerList.props.buttons.agreementsButton'), onClick: () => onView(provider) }
                        ]}
                    />
                </td>
            </tr>
        );
    };

    const renderBody = () => {
        if (showSkeleton) {
            return <GuideSkeleton rows={5} label={t('providerList.props.loadingSuppliers')} data-testid="pvl-loading" />;
        }
        if (!showError && all.length === 0) {
            return (
                <GuideEmptyState
                    icon={<Building24Regular />}
                    title={t('providerList.empty.title')}
                    text={t('providerList.empty.text')}
                    action={<Button appearance="primary" icon={<Add20Regular />} onClick={onAdd}>{t('providerList.buttons.addSuplier')}</Button>}
                    data-testid="pvl-empty"
                />
            );
        }
        if (all.length > 0 && filtered.length === 0) {
            return (
                <GuideEmptyState
                    title={t('providerList.empty.noMatchTitle')}
                    text={t('providerList.empty.noMatchText', { search: search.trim() })}
                    action={<GuideLinkButton onClick={() => onSearch('')}>{t('providerList.empty.clearSearch')}</GuideLinkButton>}
                    data-testid="pvl-no-match"
                />
            );
        }
        if (all.length === 0) return null;
        return (
            <GuideTable
                ariaLabel={t('providerListPage.providerListTitle')}
                data-testid="pvl-table"
                footer={<GuidePagination {...paginationProps} pageSizeOptions={PAGE_SIZES} />}
            >
                <thead>
                    <tr>
                        <GuideTh sort={sortProps('name')}>{t('providerList.columns.provider')}</GuideTh>
                        <GuideTh sort={sortProps('identifier')}>{t('providerList.columns.identifier')}</GuideTh>
                        <GuideTh sort={sortProps('type')}>{t('providerList.columns.type')}</GuideTh>
                        <GuideTh className={cell('actions')}>
                            <span className="pvl-sr-only">{t('providerList.props.options')}</span>
                        </GuideTh>
                    </tr>
                </thead>
                <tbody>{pageRows.map(renderRow)}</tbody>
            </GuideTable>
        );
    };

    return (
        <FileDropZone
            enabled={canAddFromInvoice && !isOpen && !fromInvoiceOpen}
            overlayText={t('providerList.fromInvoice.dropOverlay')}
            onFilesDropped={onInvoiceFiles}
            className="pvl-drop"
        >
        <div className="pvl-root" data-testid="pvl-root">
            <div className="pvl-toolbar">
                <Input
                    type="search"
                    className="pvl-search"
                    contentBefore={<Search20Regular />}
                    placeholder={t('providerList.search')}
                    aria-label={t('providerList.searchAria')}
                    value={search}
                    maxLength={200}
                    onChange={(_event, data) => onSearch(data.value)}
                    data-testid="pvl-search"
                />
                <div className="pvl-toolbar__counters">
                    <GuideCountChip label={t('providerList.toolbar.total')} count={all.length} />
                    {isSearching && <GuideCountChip label={t('providerList.toolbar.matching')} count={filtered.length} className="pvl-chip--match" />}
                </div>
                <div className="pvl-toolbar__actions">
                    {canAddFromInvoice && (
                        <>
                            <Button
                                appearance="secondary"
                                icon={<DocumentArrowUp20Regular />}
                                onClick={() => invoiceInput.current?.click()}
                                data-testid="pvl-from-invoice"
                            >
                                {t('providerList.fromInvoice.button')}
                            </Button>
                            <input
                                ref={invoiceInput}
                                type="file"
                                multiple
                                hidden
                                accept={INBOX_ACCEPT}
                                data-testid="pvl-from-invoice-input"
                                onChange={(event) => {
                                    const files = Array.from(event.target.files ?? []);
                                    event.target.value = '';
                                    onInvoiceFiles(files);
                                }}
                            />
                        </>
                    )}
                    <Button appearance="primary" icon={<Add20Regular />} onClick={onAdd} data-testid="pvl-add">
                        {t('providerList.buttons.addSuplier')}
                    </Button>
                </div>
            </div>

            {showError && (
                <GuideMessageBar intent="error" className="pvl-banner" data-testid="pvl-error">
                    <MessageBarBody>
                        <MessageBarTitle>{t('providerList.errorMessage')}</MessageBarTitle>
                        {props.error?.ErrorMessage}
                        {props.error?.Errors && props.error.Errors.length > 0 && (
                            <ul className="pvl-banner__list">
                                {props.error.Errors.map((item: string) => <li key={item}>{item}</li>)}
                            </ul>
                        )}
                    </MessageBarBody>
                    <MessageBarActions>
                        <GuideBannerAction onClick={load}>{t('providerList.retry')}</GuideBannerAction>
                    </MessageBarActions>
                </GuideMessageBar>
            )}

            {renderBody()}

            <ProviderFormComponent
                isOpen={isOpen}
                openPanel={openPanel}
                dismissPanel={onCloseForm}
                Provider={selectedProvider}
            />

            {canAddFromInvoice && (
                <ProviderFromInvoiceDrawer
                    open={fromInvoiceOpen}
                    batch={invoiceBatch}
                    onDismiss={() => setFromInvoiceOpen(false)}
                    identify={props.IdentifyProviderInvoice}
                    resolve={props.ResolveProviderInbox}
                    discard={props.DiscardProviderInbox}
                    onResolved={load}
                    onGoToProvider={(route) => navigate(route)}
                />
            )}
        </div>
        </FileDropZone>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ProvidersListComponent as any);
